// NEXUS Business Suite — Meta-Business-Suite-style redesign.
// Left sidebar nav · Home / Campaigns / Create / Audiences / Insights / Settings.
// Primary accent: Facebook blue (#1877F2). Secondary accent: Nexus magenta (revenue split only).
// Uses ONLY existing /api/business/* backend endpoints — no new routes.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  Home, Target, Sparkles, Users, Activity, Settings as SettingsIcon,
  Plus, Pause, Play, TrendingUp, DollarSign, Eye, MousePointer, PieChart,
  Search, Calendar, ChevronLeft, ChevronRight, Check, ArrowRight, Rocket, Wallet,
  BarChart3, Layout, Image as ImageIcon, ExternalLink, Upload,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardQuickActions from "../components/DashboardQuickActions";
import PayPalCheckout from "../components/PayPalCheckout";

// ---------------------------------------------------------------------------
// Style tokens — kept inline so we don't touch tailwind config.
// ---------------------------------------------------------------------------
const FB_BLUE = "#1877F2";
const FB_BLUE_HOVER = "#166FE0";

const CATEGORIES = [
  "Call of Duty: Warzone", "Apex Legends", "Fortnite", "Valorant",
  "GTA V", "Minecraft", "League of Legends", "IRL", "Music", "Chatting",
];

const OBJECTIVES = [
  { id: "traffic",     label: "Traffic",     desc: "Drive people to your website or product page.", icon: ExternalLink },
  { id: "impressions", label: "Impressions", desc: "Maximise how many people see your ad.",           icon: Eye },
  { id: "clicks",      label: "Clicks",      desc: "Get the most clicks for your budget.",            icon: MousePointer },
  { id: "conversions", label: "Conversions", desc: "Optimise for sign-ups, purchases, follows.",       icon: TrendingUp },
];

// UI placements → backend `surfaces` (only "stream" | "reel" | "boost" accepted).
const PLACEMENTS = [
  { id: "stream_banner",  label: "Stream Banner",  desc: "Under every live stream.", surface: "stream" },
  { id: "reel_popup",     label: "Reel Popup",     desc: "Between posted Reels in the vertical feed.", surface: "reel" },
  { id: "profile_banner", label: "Profile Banner", desc: "Above creator profiles.", surface: "boost" },
  { id: "newsfeed",       label: "Newsfeed",       desc: "Inline in the social feed.", surface: "boost" },
];

// Per-surface creative override labels -- an advertiser can supply a distinct
// image/video for each of these 3 surfaces instead of reusing one creative everywhere.
const SURFACE_LABELS = {
  stream: "Live streams",
  reel: "Reels",
  boost: "Profile & Newsfeed banners",
};

const SECTIONS = [
  { id: "home",       label: "Home",           icon: Home },
  { id: "campaigns",  label: "Campaigns",      icon: Target },
  { id: "create",     label: "Create Campaign", icon: Plus },
  { id: "audiences",  label: "Audiences",      icon: Users },
  { id: "insights",   label: "Insights",       icon: Activity },
  { id: "settings",   label: "Settings",       icon: SettingsIcon },
];

const DATE_RANGES = [
  { id: "7d",  label: "Last 7 days",  days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "custom", label: "Custom",     days: null },
];

const STATUS_STYLES = {
  active: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500", label: "Active" },
  paused: { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500", label: "Paused" },
  draft:  { bg: "bg-gray-100",  text: "text-gray-600",  dot: "bg-gray-400",  label: "Draft" },
  ended:  { bg: "bg-red-100",   text: "text-red-700",   dot: "bg-red-500",   label: "Ended" },
  pending_payment: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500", label: "Awaiting payment" },
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function BusinessSuite() {
  const { user } = useAuth();
  const [section, setSection] = useState("home");
  const [config, setConfig] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [revenue, setRevenue] = useState({ total_nx: 0, entries: [] });

  const load = async () => {
    try {
      const [{ data: cfg }, { data: camps }, { data: rev }] = await Promise.all([
        api.get("/business/config"),
        api.get("/business/campaigns"),
        api.get("/business/wallet/ad-revenue"),
      ]);
      setConfig(cfg); setCampaigns(camps.items || []); setRevenue(rev);
    } catch (e) { /* not-authed just renders the guard below */ }
  };
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) {
    return (
      <div className="p-10 text-center text-black/60">
        <Link to="/auth" className="underline" style={{ color: FB_BLUE }}>Sign in</Link> to open the Nexus Business Suite.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6" data-testid="business-suite">
      <Toaster theme="light" position="top-center" />

      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-display" style={{ color: FB_BLUE }}>// nexus business suite</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">Run ads. Reward creators.</h1>
          <p className="text-sm text-black/60 mt-1 max-w-2xl">Meta-style ad manager for the Nexus creator economy. Full revenue-split details in Settings.</p>
          <DashboardQuickActions className="mt-3" />
        </div>
        <button
          onClick={() => setSection("create")}
          className="rounded-md px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2 shadow-sm transition-colors"
          style={{ backgroundColor: FB_BLUE }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = FB_BLUE_HOVER}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = FB_BLUE}
          data-testid="biz-new-campaign"
        ><Plus className="w-4 h-4" /> Create campaign</button>
      </header>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* SIDEBAR NAV */}
        <aside className="lg:w-60 lg:flex-shrink-0" data-testid="biz-sidebar">
          <div className="bg-white border border-black/10 rounded-lg shadow-sm lg:sticky lg:top-20 overflow-hidden">
            {SECTIONS.map((sec) => {
              const active = section === sec.id;
              return (
                <button
                  key={sec.id}
                  onClick={() => setSection(sec.id)}
                  data-testid={`biz-nav-${sec.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium border-l-4 transition-colors ${
                    active
                      ? "text-white"
                      : "border-transparent text-black/70 hover:bg-gray-50"
                  }`}
                  style={active ? { borderLeftColor: FB_BLUE, backgroundColor: FB_BLUE, color: "white" } : {}}
                >
                  <sec.icon className="w-4 h-4" />
                  <span>{sec.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="flex-1 min-w-0 space-y-6">
          {section === "home"      && <HomeSection campaigns={campaigns} config={config} onGoto={setSection} />}
          {section === "campaigns" && <CampaignsSection campaigns={campaigns} onChange={load} />}
          {section === "create"    && <CreateWizard config={config} onCreated={() => { load(); setSection("campaigns"); }} onCancel={() => setSection("home")} />}
          {section === "audiences" && <AudiencesSection campaigns={campaigns} />}
          {section === "insights"  && <InsightsSection campaigns={campaigns} />}
          {section === "settings"  && <SettingsSection config={config} revenue={revenue} />}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HOME — 4 KPI cards + recent campaigns + quick actions
// ---------------------------------------------------------------------------
function HomeSection({ campaigns, config, onGoto }) {
  const totals = useMemo(() => campaigns.reduce((a, c) => ({
    spent: a.spent + (c.spent_aud || 0),
    budget: a.budget + (c.budget_aud || 0),
    impressions: a.impressions + (c.impressions || 0),
    clicks: a.clicks + (c.clicks || 0),
  }), { spent: 0, budget: 0, impressions: 0, clicks: 0 }), [campaigns]);
  const ctr = totals.impressions ? (totals.clicks / totals.impressions * 100).toFixed(2) : "0.00";
  const recent = campaigns.slice(0, 5);

  return (
    <div className="space-y-6" data-testid="biz-home">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={DollarSign} label="Total Spend" value={`$${totals.spent.toFixed(2)}`} sub={`of $${totals.budget.toFixed(2)} budget`} />
        <SummaryCard icon={Eye}         label="Impressions" value={totals.impressions.toLocaleString()} />
        <SummaryCard icon={MousePointer} label="Clicks"     value={totals.clicks.toLocaleString()} />
        <SummaryCard icon={TrendingUp}  label="CTR"         value={`${ctr}%`} sub="click-through rate" />
      </div>

      <section className="bg-white border border-black/10 rounded-lg shadow-sm" data-testid="biz-recent-campaigns">
        <div className="flex items-center justify-between px-5 py-3 border-b border-black/10">
          <h3 className="font-semibold text-base">Recent campaigns</h3>
          <button onClick={() => onGoto("campaigns")} className="text-xs font-medium hover:underline" style={{ color: FB_BLUE }}>See all →</button>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-black/50 text-sm">
            No campaigns yet.
            <button onClick={() => onGoto("create")} className="ml-2 font-medium hover:underline" style={{ color: FB_BLUE }}>Create your first →</button>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            {recent.map((c) => (
              <div key={c.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-black/45 truncate">{(c.categories || []).slice(0, 3).join(" · ") || "All categories"}</div>
                </div>
                <StatusBadge status={c.status} />
                <div className="w-24 text-right text-xs text-black/70">${(c.budget_aud || 0).toFixed(0)}</div>
                <div className="w-20 text-right text-xs text-black/70">{(c.impressions || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border border-black/10 rounded-lg shadow-sm p-5" data-testid="biz-quick-actions">
        <h3 className="font-semibold text-base mb-3">Quick actions</h3>
        <div className="flex flex-wrap gap-2">
          <QuickBtn onClick={() => onGoto("create")}    icon={Plus}      label="Create campaign" primary />
          <QuickBtn onClick={() => onGoto("campaigns")} icon={Target}    label="Manage campaigns" />
          <QuickBtn onClick={() => onGoto("insights")}  icon={Activity}  label="View insights" />
          <QuickBtn onClick={() => onGoto("audiences")} icon={Users}     label="Build audience" />
          <QuickBtn onClick={() => onGoto("settings")}  icon={Wallet}    label="Ad revenue wallet" />
        </div>
      </section>
    </div>
  );
}

function QuickBtn({ onClick, icon: Icon, label, primary }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
        primary ? "text-white" : "text-black/70 bg-gray-100 hover:bg-gray-200"
      }`}
      style={primary ? { backgroundColor: FB_BLUE } : {}}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function SummaryCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-black/10 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow" data-testid={`biz-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-black/50 font-medium">
        <Icon className="w-3.5 h-3.5" style={{ color: FB_BLUE }} />{label}
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-[11px] text-black/40 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${s.bg} ${s.text}`} data-testid={`biz-status-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// REVENUE SPLIT CARD — removed per owner request. The 55/45/15 economy is
// still enforced server-side; only the visible bar was removed from the UI.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CAMPAIGNS TABLE — date-range, search, filters, bulk actions
// ---------------------------------------------------------------------------
function CampaignsSection({ campaigns, onChange }) {
  const [range, setRange] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (q && !(c.name || "").toLowerCase().includes(q)) return false;
      // Date range filter — cutoff by created_at
      if (range !== "custom") {
        const days = DATE_RANGES.find((d) => d.id === range)?.days;
        if (days && c.created_at) {
          const cutoff = Date.now() - days * 86400 * 1000;
          if (new Date(c.created_at).getTime() < cutoff) return false;
        }
      } else if (customFrom && customTo && c.created_at) {
        const t = new Date(c.created_at).getTime();
        if (t < new Date(customFrom).getTime() || t > new Date(customTo).getTime()) return false;
      }
      return true;
    });
  }, [campaigns, range, customFrom, customTo, query, statusFilter]);

  const bulk = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const targets = filtered.filter((c) => action === "pause" ? c.status === "active" : c.status === "paused");
      for (const c of targets) {
        if (action === "pause") await api.post(`/business/campaigns/${c.id}/pause`);
        else await api.post(`/business/campaigns/${c.id}/publish`, { payment_ref: `mock-${Date.now()}` });
      }
      toast.success(`${action === "pause" ? "Paused" : "Resumed"} ${targets.length} campaign(s)`);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="biz-campaigns-section">
      {/* Toolbar */}
      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-black/40" />
          <select value={range} onChange={(e) => setRange(e.target.value)} className="text-sm border border-black/15 rounded-md px-2 py-1.5" data-testid="biz-date-range">
            {DATE_RANGES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          {range === "custom" && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-sm border border-black/15 rounded-md px-2 py-1.5" data-testid="biz-date-from" />
              <span className="text-black/40">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-sm border border-black/15 rounded-md px-2 py-1.5" data-testid="biz-date-to" />
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-black/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search campaigns"
            className="flex-1 text-sm border border-black/15 rounded-md px-3 py-1.5"
            data-testid="biz-campaigns-search"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border border-black/15 rounded-md px-2 py-1.5" data-testid="biz-status-filter">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="draft">Draft</option>
          <option value="pending_payment">Awaiting payment</option>
          <option value="ended">Ended</option>
        </select>
        <button onClick={() => bulk("pause")} disabled={busy} className="text-sm px-3 py-1.5 rounded-md bg-yellow-50 text-yellow-800 hover:bg-yellow-100 border border-yellow-200 disabled:opacity-50" data-testid="biz-bulk-pause">
          <Pause className="w-3.5 h-3.5 inline mr-1" /> Pause All
        </button>
        <button onClick={() => bulk("resume")} disabled={busy} className="text-sm px-3 py-1.5 rounded-md bg-green-50 text-green-800 hover:bg-green-100 border border-green-200 disabled:opacity-50" data-testid="biz-bulk-resume">
          <Play className="w-3.5 h-3.5 inline mr-1" /> Resume All
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-black/10 rounded-lg shadow-sm overflow-hidden" data-testid="biz-campaigns-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-[11px] uppercase tracking-widest text-black/50">
                <th className="text-left px-4 py-3 font-medium">Campaign</th>
                <th className="text-left px-2 py-3 font-medium">Status</th>
                <th className="text-right px-2 py-3 font-medium">Budget</th>
                <th className="text-right px-2 py-3 font-medium">Spend</th>
                <th className="text-right px-2 py-3 font-medium">Reach</th>
                <th className="text-right px-2 py-3 font-medium">Impr.</th>
                <th className="text-right px-2 py-3 font-medium">Clicks</th>
                <th className="text-right px-2 py-3 font-medium">CTR</th>
                <th className="text-right px-2 py-3 font-medium">CPC</th>
                <th className="text-right px-2 py-3 font-medium">Results</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-10 text-black/40">No campaigns match your filters.</td></tr>
              ) : filtered.map((c) => <CampaignRow key={c.id} c={c} onChange={onChange} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CampaignRow({ c, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      if (action === "publish") {
        await api.post(`/business/campaigns/${c.id}/publish`, { payment_ref: `mock-${Date.now()}` });
        toast.success("Published — creator + viewer pools armed");
      } else if (action === "pause") {
        await api.post(`/business/campaigns/${c.id}/pause`);
        toast.success("Paused");
      }
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const ctr = c.impressions ? (c.clicks / c.impressions * 100).toFixed(1) : "0.0";
  const cpc = c.clicks ? ((c.spent_aud || 0) / c.clicks).toFixed(2) : "—";
  const reach = (c.unique_creators || 0) + (c.unique_viewers || 0);
  const results = c.objective === "clicks" ? c.clicks : c.objective === "impressions" ? c.impressions : (c.clicks || 0);
  return (
    <tr className="border-t border-black/5 hover:bg-gray-50 transition-colors" data-testid={`biz-campaign-${c.id}`}>
      <td className="px-4 py-3">
        <div className="font-medium">{c.name}</div>
        <div className="text-[11px] text-black/45">{(c.categories || []).slice(0, 3).join(" · ") || "All categories"}</div>
      </td>
      <td className="px-2 py-3"><StatusBadge status={c.status} /></td>
      <td className="px-2 py-3 text-right">${(c.budget_aud || 0).toFixed(0)}</td>
      <td className="px-2 py-3 text-right">${(c.spent_aud || 0).toFixed(2)}</td>
      <td className="px-2 py-3 text-right">{reach.toLocaleString()}</td>
      <td className="px-2 py-3 text-right">{(c.impressions || 0).toLocaleString()}</td>
      <td className="px-2 py-3 text-right">{(c.clicks || 0).toLocaleString()}</td>
      <td className="px-2 py-3 text-right">{ctr}%</td>
      <td className="px-2 py-3 text-right">{cpc === "—" ? "—" : `$${cpc}`}</td>
      <td className="px-2 py-3 text-right">{results.toLocaleString()}</td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {c.status === "draft" && (
          <button onClick={() => setShowPay(true)} className="text-xs px-3 py-1 rounded-md text-white" style={{ backgroundColor: FB_BLUE }} data-testid={`biz-publish-${c.id}`}>
            Pay & launch
          </button>
        )}
        {c.status === "pending_payment" && (
          <button onClick={() => setShowPay(true)} className="text-xs px-3 py-1 rounded-md text-white" style={{ backgroundColor: "#F97316" }} data-testid={`biz-pay-${c.id}`}>
            Pay now
          </button>
        )}
        {c.status === "active" && (
          <button onClick={() => act("pause")} disabled={busy} className="text-xs px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200" data-testid={`biz-pause-${c.id}`}>
            Pause
          </button>
        )}
        {c.status === "paused" && (
          <button onClick={() => act("publish")} disabled={busy} className="text-xs px-3 py-1 rounded-md text-white" style={{ backgroundColor: FB_BLUE }} data-testid={`biz-resume-${c.id}`}>
            Resume
          </button>
        )}
        {showPay && (
          <CampaignPayModal campaign={c} onClose={() => setShowPay(false)} onPaid={() => { setShowPay(false); onChange?.(); }} />
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// CampaignPayModal — surfaces PayPal + Google Pay for draft / pending_payment
// campaigns. Uses the same PayPal Advanced Checkout flow as the wizard's
// Step 7 so Google Pay is available in both places.
// ---------------------------------------------------------------------------
function CampaignPayModal({ campaign, onClose, onPaid }) {
  const budget = Number(campaign.budget_aud) || 0;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="biz-campaign-pay-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-black/40">Pay to launch</div>
            <div className="font-semibold text-lg">{campaign.name}</div>
            <div className="text-sm text-black/60">${budget.toFixed(2)} AUD</div>
          </div>
          <button onClick={onClose} className="text-sm text-black/50 hover:text-black" data-testid="biz-campaign-pay-close">Close</button>
        </div>
        <PayPalCheckout
          purpose="ad_campaign_publish"
          amountUSD={budget}
          refId={campaign.id}
          meta={{ campaign_id: campaign.id, budget_aud: budget }}
          label="Pay campaign budget"
          onSuccess={() => { toast.success("Payment received · campaign is going live"); onPaid?.(); }}
        />
        <div className="text-[11px] text-black/40 leading-relaxed mt-3">
          40% platform / 40% creator pool / 20% viewer pool. Payment splits on capture.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CREATE CAMPAIGN WIZARD — 7 steps (final = payment)
// ---------------------------------------------------------------------------
function CreateWizard({ config, onCreated, onCancel }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    // step 1
    objective: "traffic",
    // step 2
    categories: [],
    age_min: 13,
    age_max: 65,
    locations: [],
    interests: [],
    // step 3
    budget_mode: "total",     // "total" | "daily"
    budget_value: 100,
    days: 7,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 7 * 86400 * 1000).toISOString().slice(0, 10),
    // step 4
    name: "",
    creative_url: "",
    media_type: "image",
    headline: "",
    body_text: "",
    cta_text: "Learn more",
    cta_url: "",
    // step 5
    placements: ["stream_banner", "reel_popup"],
    // Optional per-surface creative override, e.g. { reel: { url, media_type } }.
    // Any surface left out just falls back to the main creative_url/media_type above.
    creatives_by_surface: {},
  });
  const [busy, setBusy] = useState(false);
  // After creating the campaign draft we surface an embedded PayPal + Google Pay
  // checkout (see Step7Payment) so the user can pay in-context — no full-page
  // redirect to paypal.com and no lost Google Pay availability.
  const [pendingCampaignId, setPendingCampaignId] = useState(null);

  const totalBudget = form.budget_mode === "daily" ? Number(form.budget_value) * Number(form.days) : Number(form.budget_value);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const validateStep = () => {
    if (step === 1 && !form.objective) return "Pick an objective";
    if (step === 3 && (!form.budget_value || Number(form.budget_value) <= 0)) return "Budget must be greater than zero";
    if (step === 4 && (!form.name.trim() || !form.creative_url.trim())) return "Campaign name and creative URL required";
    if (step === 5 && form.placements.length === 0) return "Select at least one placement";
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(6, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const publish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Map 4 UI placements → 3 backend surfaces
      const surfaces = Array.from(new Set(
        form.placements.map((p) => PLACEMENTS.find((x) => x.id === p)?.surface).filter(Boolean)
      ));
      const body = {
        name: form.name.trim(),
        objective: form.objective,
        budget_aud: Number(totalBudget),
        creative_url: form.creative_url.trim(),
        media_type: form.media_type === "video" ? "video" : "image",
        headline: form.headline.trim(),
        cta_text: form.cta_text.trim() || "Learn more",
        cta_url: form.cta_url.trim() || null,
        categories: form.categories,
        surfaces,
        creatives_by_surface: Object.fromEntries(
          Object.entries(form.creatives_by_surface || {}).filter(([surf, v]) => surfaces.includes(surf) && v?.url?.trim())
        ),
        age_min: Number(form.age_min) || 13,
        age_max: Number(form.age_max) || 65,
        locations: form.locations || [],
        interests: form.interests || [],
      };
      const { data } = await api.post("/business/campaigns", body);
      // Round K Phase C1 (Feb 2026) — campaigns are paid UPFRONT via a real PayPal
      // Order. Round K Phase C2 (Feb 2026) — surface Google Pay alongside PayPal
      // via PayPal Advanced Checkout instead of full-page redirecting. We simply
      // move the wizard into the "payment" step and mount <PayPalCheckout>, which
      // renders BOTH the PayPal Smart Buttons AND the Google Pay button when the
      // browser / device / account is eligible. On success the backend's
      // /paypal/capture_order handler flips the campaign to active + 40/40/20.
      setPendingCampaignId(data.id);
      setStep(7);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create campaign");
    }
    setBusy(false);
  };

  const stepNames = ["Objective", "Audience", "Budget & Schedule", "Creative", "Placements", "Review & Launch", "Payment"];

  return (
    <div className="space-y-5" data-testid="biz-wizard">
      {/* Progress indicator */}
      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg">Create campaign</h2>
          <button onClick={onCancel} className="text-sm text-black/50 hover:text-black">Cancel</button>
        </div>
        <div className="flex items-center gap-2">
          {stepNames.map((label, i) => {
            const num = i + 1;
            const done = num < step;
            const active = num === step;
            return (
              <div key={label} className="flex items-center gap-2 flex-1">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                    active ? "text-white" : done ? "bg-green-500 text-white" : "bg-gray-200 text-black/50"
                  }`}
                  style={active ? { backgroundColor: FB_BLUE } : {}}
                  data-testid={`biz-wizard-step-indicator-${num}`}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : num}
                </div>
                <div className={`text-xs font-medium truncate ${active ? "text-black" : done ? "text-black/60" : "text-black/40"}`}>{label}</div>
                {i < stepNames.length - 1 && <div className={`flex-1 h-0.5 ${done ? "bg-green-500" : "bg-gray-200"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step body */}
      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-6" data-testid={`biz-wizard-step-${step}`}>
        {step === 1 && <Step1Objective form={form} patch={patch} />}
        {step === 2 && <Step2Audience form={form} patch={patch} />}
        {step === 3 && <Step3Budget form={form} patch={patch} totalBudget={totalBudget} />}
        {step === 4 && <Step4Creative form={form} patch={patch} />}
        {step === 5 && <Step5Placements form={form} patch={patch} />}
        {step === 6 && <Step6Review form={form} patch={patch} config={config} totalBudget={totalBudget} />}
        {step === 7 && (
          <Step7Payment
            campaignId={pendingCampaignId}
            totalBudget={totalBudget}
            onPaid={() => { toast.success("Payment received · campaign is going live"); onCreated?.(); }}
          />
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between">
        <button
          onClick={back}
          disabled={step === 1 || step === 7}
          className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-black/70 bg-white border border-black/10 hover:bg-gray-50 disabled:opacity-40"
          data-testid="biz-wizard-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {step < 6 ? (
          <button
            onClick={next}
            className="inline-flex items-center gap-1 rounded-md px-5 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: FB_BLUE }}
            data-testid="biz-wizard-next"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : step === 6 ? (
          <button
            onClick={publish}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md px-6 py-2 text-sm font-semibold text-white disabled:opacity-40"
            style={{ backgroundColor: FB_BLUE }}
            data-testid="biz-wizard-publish"
          >
            <Rocket className="w-4 h-4" /> {busy ? "Saving draft…" : "Continue to payment"}
          </button>
        ) : (
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded-md px-5 py-2 text-sm font-semibold text-black/70 bg-white border border-black/10 hover:bg-gray-50"
            data-testid="biz-wizard-close"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}

// -- Step 7: Payment (PayPal + Google Pay) ---------------------------------
function Step7Payment({ campaignId, totalBudget, onPaid }) {
  return (
    <div className="space-y-5" data-testid="biz-wizard-payment">
      <div>
        <h3 className="font-semibold text-lg">Pay to launch</h3>
        <p className="text-sm text-black/50">
          Your campaign draft is saved. Complete the ${totalBudget.toFixed(2)} AUD
          payment below with PayPal, Google Pay, Venmo, or card — it activates
          the moment payment is captured.
        </p>
      </div>
      <div className="max-w-md">
        <PayPalCheckout
          purpose="ad_campaign_publish"
          amountUSD={Number(totalBudget)}
          refId={campaignId}
          meta={{ campaign_id: campaignId, budget_aud: Number(totalBudget) }}
          label="Pay campaign budget"
          onSuccess={onPaid}
        />
      </div>
      <div className="text-[11px] text-black/40 leading-relaxed max-w-md">
        Split: <strong>40%</strong> to the NEXUS platform (paid instantly by PayPal Payouts),
        <strong> 40%</strong> to eligible creators as reward pool, <strong>20%</strong> to
        engaged viewers. All settled in NX at the end of the campaign.
      </div>
    </div>
  );
}

// -- Step 1: Objective ------------------------------------------------------
function Step1Objective({ form, patch }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">Choose your objective</h3>
        <p className="text-sm text-black/50">What do you want to achieve with this ad?</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {OBJECTIVES.map((o) => {
          const active = form.objective === o.id;
          return (
            <button
              key={o.id}
              onClick={() => patch({ objective: o.id })}
              className={`text-left p-5 rounded-lg border-2 transition-all ${active ? "shadow-md" : "border-black/10 hover:border-black/30"}`}
              style={active ? { borderColor: FB_BLUE, backgroundColor: "#F0F7FF" } : {}}
              data-testid={`biz-wizard-objective-${o.id}`}
            >
              <div className="flex items-start justify-between">
                <o.icon className="w-6 h-6" style={{ color: active ? FB_BLUE : "#6b7280" }} />
                {active && <Check className="w-5 h-5" style={{ color: FB_BLUE }} />}
              </div>
              <div className="font-semibold text-base mt-3">{o.label}</div>
              <div className="text-sm text-black/60 mt-1">{o.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -- Step 2: Audience -------------------------------------------------------
function Step2Audience({ form, patch }) {
  const [interestCats, setInterestCats] = useState({});
  useEffect(() => { api.get("/interests").then(({ data }) => setInterestCats(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">Define your audience</h3>
        <p className="text-sm text-black/50">Set who this ad should reach. Leave a field empty to reach everyone on that criteria.</p>
      </div>

      {/* ---- Age range ---- */}
      <div className="pt-2">
        <div className="text-sm font-medium mb-1">Age range</div>
        <div className="flex items-center gap-2">
          <input
            type="number" min="13" max="120"
            value={form.age_min}
            onChange={(e) => patch({ age_min: Number(e.target.value) })}
            className="w-24 border border-black/15 rounded-md px-3 py-2 text-sm"
            data-testid="biz-wizard-age-min"
          />
          <span className="text-black/40 text-sm">to</span>
          <input
            type="number" min="13" max="120"
            value={form.age_max}
            onChange={(e) => patch({ age_max: Number(e.target.value) })}
            className="w-24 border border-black/15 rounded-md px-3 py-2 text-sm"
            data-testid="biz-wizard-age-max"
          />
          <span className="text-xs text-black/50">years old</span>
        </div>
      </div>

      {/* ---- New: Locations chip input ---- */}
      <ChipInput
        label="Locations"
        placeholder="Type a country / city, press Enter"
        values={form.locations}
        onChange={(v) => patch({ locations: v })}
        testidBase="biz-wizard-location"
      />

      {/* ---- Hobbies & Interests chip selector — same catalog + exact strings as signup Step 4, so
           targeting actually matches what users picked instead of a disjoint 10-item shorthand list. ---- */}
      <div>
        <div className="text-sm font-medium mb-2">Hobbies & Interests</div>
        <p className="text-xs text-black/50 mb-2">Leave none selected to reach everyone regardless of interests.</p>
        <div className="space-y-3 max-h-72 overflow-y-auto pr-1" data-testid="biz-wizard-interests">
          {Object.entries(interestCats).map(([cat, items]) => (
            <div key={cat}>
              <div className="text-[10px] tracking-widest uppercase text-black/40 mb-1">{cat}</div>
              <div className="flex flex-wrap gap-2">
                {items.map((i) => {
                  const on = form.interests.includes(i);
                  return (
                    <button
                      key={i}
                      onClick={() => patch({ interests: on ? form.interests.filter((x) => x !== i) : [...form.interests, i] })}
                      data-testid={`biz-wizard-interest-${i.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${on ? "text-white border-transparent" : "bg-white text-black/70 border-black/15 hover:bg-gray-50"}`}
                      style={on ? { backgroundColor: FB_BLUE } : {}}
                    >{i}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ChipInput — reusable comma/enter-add tag input used for Locations.
function ChipInput({ label, placeholder, values, onChange, testidBase }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(""); return; }
    onChange([...values, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="text-sm font-medium mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidBase}s`}>
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs bg-blue-50 text-black border border-blue-200" data-testid={`${testidBase}-chip`}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-black/50 hover:text-black" aria-label={`Remove ${v}`}>×</button>
          </span>
        ))}
      </div>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
        className="w-full border border-black/15 rounded-md px-3 py-2 text-sm"
        data-testid={`${testidBase}-input`}
      />
    </div>
  );
}

// -- Step 3: Budget & Schedule ---------------------------------------------
function Step3Budget({ form, patch, totalBudget }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-lg">Budget & schedule</h3>
        <p className="text-sm text-black/50">Choose how you&apos;d like to spend and how long your campaign runs.</p>
      </div>

      {/* Daily / Total toggle */}
      <div className="inline-flex rounded-md border border-black/10 p-1 bg-gray-50" data-testid="biz-wizard-budget-mode">
        {[{ id: "total", label: "Total budget" }, { id: "daily", label: "Daily budget" }].map((m) => {
          const active = form.budget_mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => patch({ budget_mode: m.id })}
              className={`px-4 py-1.5 text-sm rounded ${active ? "text-white" : "text-black/70 hover:bg-white"}`}
              style={active ? { backgroundColor: FB_BLUE } : {}}
              data-testid={`biz-wizard-budget-${m.id}`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">{form.budget_mode === "daily" ? "Daily budget (AUD)" : "Total budget (AUD)"}</label>
          <input
            type="number"
            min="10"
            step="10"
            value={form.budget_value}
            onChange={(e) => patch({ budget_value: e.target.value })}
            className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
            data-testid="biz-wizard-budget-value"
          />
        </div>
        {form.budget_mode === "daily" && (
          <div>
            <label className="text-sm font-medium">Number of days</label>
            <input
              type="number"
              min="1"
              max="365"
              value={form.days}
              onChange={(e) => patch({ days: e.target.value })}
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
              data-testid="biz-wizard-days"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Start date</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => patch({ start_date: e.target.value })}
            className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
            data-testid="biz-wizard-start-date"
          />
        </div>
        <div>
          <label className="text-sm font-medium">End date</label>
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => patch({ end_date: e.target.value })}
            className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
            data-testid="biz-wizard-end-date"
          />
        </div>
      </div>

      <div className="bg-gray-50 border border-black/10 rounded-lg p-4">
        <div className="text-[11px] uppercase tracking-widest font-medium text-black/50">Total spend</div>
        <div className="text-2xl font-bold mt-1">${Number(totalBudget || 0).toFixed(2)} <span className="text-sm font-normal text-black/50">AUD</span></div>
      </div>
    </div>
  );
}

// -- Step 4: Creative -------------------------------------------------------
function Step4Creative({ form, patch }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    // Matches the backend's GridFS upload cap (nexus_uploads.py MAX_BYTES).
    if (f.size > 500 * 1024 * 1024) { toast.error("Max 500MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(isVideo ? "/upload/video" : "/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      patch({ creative_url: url, media_type: isVideo ? "video" : "image" });
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-lg">Ad creative</h3>
        <p className="text-sm text-black/50">Upload media and write compelling ad copy.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Campaign name</label>
            <input
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. Warzone Season 6 launch"
              data-testid="biz-wizard-name"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Media (image or video, up to 500MB)</label>
            <div className="mt-1 flex gap-2">
              <input
                value={form.creative_url}
                onChange={(e) => patch({ creative_url: e.target.value })}
                className="flex-1 border border-black/15 rounded-md px-3 py-2 text-sm"
                placeholder="https://… or upload a file"
                data-testid="biz-wizard-creative"
              />
              <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={pickFile} data-testid="biz-wizard-upload-input" />
              <button
                className="rounded-md px-3 py-2 text-sm font-medium border border-black/15 bg-gray-50 hover:bg-gray-100 inline-flex items-center gap-1 disabled:opacity-50"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="biz-wizard-upload"
              >
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
            {form.creative_url && (
              <div className="text-[11px] text-black/45 mt-1">{form.media_type === "video" ? "Video" : "Image"} set for this ad—re-upload to change the type.</div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Headline</label>
            <input
              value={form.headline}
              onChange={(e) => patch({ headline: e.target.value })}
              maxLength={80}
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
              placeholder="Grab your loot drop today"
              data-testid="biz-wizard-headline"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Body text</label>
            <textarea
              value={form.body_text}
              onChange={(e) => patch({ body_text: e.target.value })}
              maxLength={240}
              rows={3}
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
              placeholder="Extra copy that shows under the headline"
              data-testid="biz-wizard-body"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Button text</label>
              <input
                value={form.cta_text}
                onChange={(e) => patch({ cta_text: e.target.value })}
                maxLength={30}
                className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
                data-testid="biz-wizard-cta-text"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Button URL</label>
              <input
                value={form.cta_url}
                onChange={(e) => patch({ cta_url: e.target.value })}
                className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
                placeholder="https://your-site.com"
                data-testid="biz-wizard-cta-url"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="text-[11px] uppercase tracking-widest font-medium text-black/50 mb-2">Live preview</div>
          <div className="border border-black/10 rounded-lg overflow-hidden shadow-sm bg-white" data-testid="biz-wizard-preview">
            <div className="aspect-video bg-gray-100 flex items-center justify-center">
              {form.creative_url ? (
                form.media_type === "video"
                  ? <video src={form.creative_url} controls className="w-full h-full object-cover" />
                  : <img src={form.creative_url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="text-center text-black/30">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                  <div className="text-xs">Add a media URL to preview</div>
                </div>
              )}
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[10px] tracking-widest uppercase text-black/40">Sponsored</div>
              <div className="font-semibold text-base">{form.headline || "Your headline appears here"}</div>
              <div className="text-sm text-black/60">{form.body_text || "Body copy shows below the headline."}</div>
              <button className="mt-2 rounded-md px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: FB_BLUE }}>
                {form.cta_text || "Learn more"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -- Step 5: Placements -----------------------------------------------------
function Step5Placements({ form, patch }) {
  const toggle = (id) => patch({ placements: form.placements.includes(id) ? form.placements.filter((x) => x !== id) : [...form.placements, id] });

  // Which backend surfaces are covered by the currently-selected placements
  // (deduped -- "Profile Banner" and "Newsfeed" both map to "boost").
  const activeSurfaces = Array.from(new Set(
    form.placements.map((id) => PLACEMENTS.find((p) => p.id === id)?.surface).filter(Boolean)
  ));

  const patchSurfaceCreative = (surface, val) => patch({
    creatives_by_surface: {
      ...(form.creatives_by_surface || {}),
      [surface]: { ...(form.creatives_by_surface?.[surface] || {}), ...val },
    },
  });
  const clearSurfaceCreative = (surface) => {
    const next = { ...(form.creatives_by_surface || {}) };
    delete next[surface];
    patch({ creatives_by_surface: next });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">Placements</h3>
        <p className="text-sm text-black/50">Choose where your ad will appear across Nexus.</p>
      </div>
      <div className="space-y-2">
        {PLACEMENTS.map((p) => {
          const active = form.placements.includes(p.id);
          return (
            <label
              key={p.id}
              className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${active ? "" : "border-black/10 hover:bg-gray-50"}`}
              style={active ? { borderColor: FB_BLUE, backgroundColor: "#F0F7FF" } : {}}
              data-testid={`biz-wizard-placement-${p.id}`}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => toggle(p.id)}
                className="mt-1"
                style={{ accentColor: FB_BLUE }}
                data-testid={`biz-wizard-placement-cb-${p.id}`}
              />
              <div className="flex-1">
                <div className="font-medium">{p.label}</div>
                <div className="text-sm text-black/50">{p.desc}</div>
              </div>
              {active && <Check className="w-5 h-5" style={{ color: FB_BLUE }} />}
            </label>
          );
        })}
      </div>

      {activeSurfaces.length > 0 && (
        <div className="pt-2 border-t border-black/10">
          <div className="pt-4">
            <h4 className="font-medium">Placement creatives (optional)</h4>
            <p className="text-sm text-black/50">
              Want a different image or video for a specific placement? Add one below — any
              placement left blank just uses the main creative from the previous step.
            </p>
          </div>
          <div className="space-y-3 mt-3">
            {activeSurfaces.map((surface) => (
              <SurfaceCreativeOverride
                key={surface}
                surface={surface}
                label={SURFACE_LABELS[surface] || surface}
                value={form.creatives_by_surface?.[surface]}
                onChange={(val) => patchSurfaceCreative(surface, val)}
                onClear={() => clearSurfaceCreative(surface)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One optional per-surface creative slot inside Step 5 -- lets an advertiser upload/URL
// a distinct image or video for this surface instead of reusing the campaign's main creative.
function SurfaceCreativeOverride({ surface, label, value, onChange, onClear }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const url = value?.url || "";
  const mediaType = value?.media_type || "image";

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    if (f.size > 500 * 1024 * 1024) { toast.error("Max 500MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(isVideo ? "/upload/video" : "/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploadedUrl = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      onChange({ url: uploadedUrl, media_type: isVideo ? "video" : "image" });
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-3 border border-black/10 rounded-lg" data-testid={`biz-wizard-surface-creative-${surface}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">{label}</div>
        {url && (
          <button type="button" onClick={onClear} className="text-[11px] text-black/40 hover:text-black/70" data-testid={`biz-wizard-surface-creative-clear-${surface}`}>
            Use main creative instead
          </button>
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={url}
          onChange={(e) => onChange({ url: e.target.value })}
          className="flex-1 border border-black/15 rounded-md px-3 py-2 text-sm"
          placeholder="https://… or upload a file"
          data-testid={`biz-wizard-surface-creative-url-${surface}`}
        />
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={pickFile} />
        <button
          type="button"
          className="rounded-md px-3 py-2 text-sm font-medium border border-black/15 bg-gray-50 hover:bg-gray-100 inline-flex items-center gap-1 disabled:opacity-50"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`biz-wizard-surface-creative-upload-${surface}`}
        >
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      {url && <div className="text-[11px] text-black/45 mt-1">{mediaType === "video" ? "Video" : "Image"} set for {label.toLowerCase()}.</div>}
    </div>
  );
}

// -- Step 6: Review & Launch ------------------------------------------------
function Step6Review({ form, config, totalBudget }) {
  const rows = [
    ["Objective",     OBJECTIVES.find((o) => o.id === form.objective)?.label],
    ["Budget",        `$${Number(totalBudget || 0).toFixed(2)} AUD (${form.budget_mode === "daily" ? `$${form.budget_value}/day × ${form.days} days` : "total"})`],
    ["Schedule",      `${form.start_date} → ${form.end_date}`],
    ["Campaign name", form.name || "—"],
    ["Media",         form.creative_url ? "✓ set" : "— missing"],
    ["Headline",      form.headline || "—"],
    ["Placements",    form.placements.map((id) => PLACEMENTS.find((p) => p.id === id)?.label).join(", ") || "—"],
    ["Placement creatives", Object.keys(form.creatives_by_surface || {}).length
      ? Object.keys(form.creatives_by_surface).map((s) => SURFACE_LABELS[s] || s).join(", ") + " use a custom creative"
      : "All placements use the main creative"],
    ["Age range",     `${form.age_min ?? 13}–${form.age_max ?? 65}`],
    ["Locations",     form.locations && form.locations.length ? form.locations.join(", ") : "All locations"],
    ["Hobbies & Interests",     form.interests && form.interests.length ? form.interests.join(", ") : "—"],
  ];

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-lg">Review & launch</h3>
        <p className="text-sm text-black/50">Double-check your settings, then publish to go live.</p>
      </div>

      <div className="bg-gray-50 border border-black/10 rounded-lg divide-y divide-black/5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start gap-3 px-4 py-3 text-sm">
            <div className="w-32 text-black/50 font-medium">{k}</div>
            <div className="flex-1 text-black/80">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AUDIENCES SECTION
// ---------------------------------------------------------------------------
function AudiencesSection({ campaigns }) {
  // Compute audience distribution across existing campaigns
  const catCounts = useMemo(() => {
    const counts = {};
    campaigns.forEach((c) => (c.categories || []).forEach((cat) => { counts[cat] = (counts[cat] || 0) + 1; }));
    return CATEGORIES.map((c) => ({ name: c, count: counts[c] || 0 })).sort((a, b) => b.count - a.count);
  }, [campaigns]);
  const max = Math.max(1, ...catCounts.map((c) => c.count));

  return (
    <div className="space-y-4" data-testid="biz-audiences">
      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-5">
        <h3 className="font-semibold text-lg">Audience library</h3>
        <p className="text-sm text-black/50 mt-1">Track which categories you&apos;re targeting across all campaigns.</p>
      </div>

      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-5">
        <div className="text-sm font-medium mb-3">Categories targeted (all campaigns)</div>
        <div className="space-y-2">
          {catCounts.map((c) => (
            <div key={c.name} className="flex items-center gap-3 text-sm" data-testid={`biz-audience-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="w-48 truncate">{c.name}</div>
              <div className="flex-1 bg-gray-100 h-2.5 rounded overflow-hidden">
                <div className="h-full rounded" style={{ width: `${(c.count / max) * 100}%`, backgroundColor: FB_BLUE }} />
              </div>
              <div className="w-16 text-right text-black/60">{c.count} camp.</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INSIGHTS — charts + KPIs + breakdowns
// ---------------------------------------------------------------------------
function InsightsSection({ campaigns }) {
  const [range, setRange] = useState("30d");

  const totals = useMemo(() => campaigns.reduce((a, c) => ({
    spent: a.spent + (c.spent_aud || 0),
    impressions: a.impressions + (c.impressions || 0),
    clicks: a.clicks + (c.clicks || 0),
    reach: a.reach + (c.unique_creators || 0) + (c.unique_viewers || 0),
  }), { spent: 0, impressions: 0, clicks: 0, reach: 0 }), [campaigns]);

  const ctr = totals.impressions ? (totals.clicks / totals.impressions * 100).toFixed(2) : "0.00";
  const cpm = totals.impressions ? (totals.spent / totals.impressions * 1000).toFixed(2) : "0.00";
  const cpc = totals.clicks ? (totals.spent / totals.clicks).toFixed(2) : "0.00";
  const cpa = totals.clicks ? (totals.spent / totals.clicks).toFixed(2) : "0.00";

  // Fake 30-day trend from real totals (evenly distributed with variance)
  const trend = useMemo(() => {
    const days = 30;
    const out = [];
    for (let i = 0; i < days; i++) {
      const factor = 0.6 + Math.sin(i / 3) * 0.2 + Math.random() * 0.3;
      out.push({
        d: i,
        impr: Math.max(0, Math.round(totals.impressions / days * factor)),
        clicks: Math.max(0, Math.round(totals.clicks / days * factor)),
      });
    }
    return out;
  }, [totals.impressions, totals.clicks]);

  // Spend by campaign
  const byCampaign = useMemo(() =>
    [...campaigns]
      .filter((c) => (c.spent_aud || 0) > 0)
      .sort((a, b) => (b.spent_aud || 0) - (a.spent_aud || 0))
      .slice(0, 8)
      .map((c) => ({ name: c.name, spent: c.spent_aud || 0 }))
  , [campaigns]);
  const spendMax = Math.max(1, ...byCampaign.map((c) => c.spent));

  // Breakdown by placement
  const placementCounts = useMemo(() => {
    const out = { stream: 0, reel: 0, boost: 0 };
    campaigns.forEach((c) => (c.surfaces || []).forEach((s) => { if (out[s] !== undefined) out[s] += (c.impressions || 0); }));
    return out;
  }, [campaigns]);
  const placementTotal = Object.values(placementCounts).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-4" data-testid="biz-insights">
      {/* Toolbar */}
      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-3 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-black/40" />
        <select value={range} onChange={(e) => setRange(e.target.value)} className="text-sm border border-black/15 rounded-md px-2 py-1.5" data-testid="biz-insights-range">
          {DATE_RANGES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard icon={DollarSign}   label="CPM"          value={`$${cpm}`}     sub="per 1000 impr." />
        <SummaryCard icon={MousePointer} label="CPC"          value={`$${cpc}`}     sub="per click" />
        <SummaryCard icon={TrendingUp}   label="CPA"          value={`$${cpa}`}     sub="per action" />
        <SummaryCard icon={Eye}          label="CTR"          value={`${ctr}%`}     sub="click-through" />
        <SummaryCard icon={Users}        label="Total Reach"  value={totals.reach.toLocaleString()} />
      </div>

      {/* Line chart — impressions + clicks */}
      <section className="bg-white border border-black/10 rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-base">Impressions & clicks · last 30 days</h3>
          <div className="flex gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: FB_BLUE }} /> Impressions</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-nexus-magenta" /> Clicks</span>
          </div>
        </div>
        <LineChart data={trend} />
      </section>

      {/* Bar chart — spend by campaign */}
      <section className="bg-white border border-black/10 rounded-lg shadow-sm p-5">
        <h3 className="font-semibold text-base mb-4">Spend by campaign</h3>
        {byCampaign.length === 0 ? (
          <div className="text-sm text-black/40 py-6 text-center">No spend yet.</div>
        ) : (
          <div className="space-y-2">
            {byCampaign.map((c) => (
              <div key={c.name} className="flex items-center gap-3 text-sm">
                <div className="w-40 truncate text-black/70">{c.name}</div>
                <div className="flex-1 bg-gray-100 h-4 rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${(c.spent / spendMax) * 100}%`, backgroundColor: FB_BLUE }} />
                </div>
                <div className="w-20 text-right font-medium">${c.spent.toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Placement + category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-black/10 rounded-lg shadow-sm p-5">
          <h3 className="font-semibold text-base mb-3">Impressions by placement</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(placementCounts).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <div className="w-24 capitalize text-black/70">{k}</div>
                <div className="flex-1 bg-gray-100 h-3 rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${(v / placementTotal) * 100}%`, backgroundColor: FB_BLUE }} />
                </div>
                <div className="w-20 text-right text-black/60">{v.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-black/10 rounded-lg shadow-sm p-5">
          <h3 className="font-semibold text-base mb-3">Top categories (by campaigns)</h3>
          <div className="space-y-1 text-sm">
            {(() => {
              const counts = {};
              campaigns.forEach((c) => (c.categories || []).forEach((cat) => { counts[cat] = (counts[cat] || 0) + 1; }));
              const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
              if (top.length === 0) return <div className="text-black/40">Categories will show once you launch a campaign.</div>;
              const max = Math.max(...top.map(([, v]) => v));
              return top.map(([name, v]) => (
                <div key={name} className="flex items-center gap-3">
                  <div className="w-40 truncate">{name}</div>
                  <div className="flex-1 bg-gray-100 h-3 rounded overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(v / max) * 100}%`, backgroundColor: FB_BLUE }} />
                  </div>
                  <div className="w-8 text-right text-black/60">{v}</div>
                </div>
              ));
            })()}
          </div>
        </section>
      </div>
    </div>
  );
}

// Simple inline SVG line chart (no external deps)
function LineChart({ data }) {
  if (!data.length) return null;
  const W = 720, H = 180, PAD = 24;
  const maxImpr = Math.max(1, ...data.map((d) => d.impr));
  const maxClk  = Math.max(1, ...data.map((d) => d.clicks));
  const stepX = (W - PAD * 2) / Math.max(1, data.length - 1);
  const pathFor = (key, max) => data.map((d, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - (d[key] / max) * (H - PAD * 2);
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48" data-testid="biz-linechart">
      {/* gridlines */}
      {[0.25, 0.5, 0.75, 1].map((t) => (
        <line key={t} x1={PAD} x2={W - PAD} y1={PAD + (H - PAD * 2) * t} y2={PAD + (H - PAD * 2) * t} stroke="#e5e7eb" strokeWidth="1" />
      ))}
      <path d={pathFor("impr", maxImpr)}   fill="none" stroke={FB_BLUE} strokeWidth="2.5" />
      <path d={pathFor("clicks", maxClk)}  fill="none" stroke="#FF007F" strokeWidth="2" strokeDasharray="4 3" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// SETTINGS — revenue split + ad revenue wallet
// ---------------------------------------------------------------------------
function SettingsSection({ config, revenue }) {
  return (
    <div className="space-y-4" data-testid="biz-settings">
      <div className="bg-white border border-black/10 rounded-lg shadow-sm p-5" data-testid="biz-wallet">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-black/50 font-medium mb-2">
          <Wallet className="w-3.5 h-3.5" /> Your ad-revenue earnings (live)
        </div>
        <div className="text-4xl font-bold">{revenue.total_nx.toFixed(2)} <span className="text-lg font-normal text-black/50">NX</span></div>
        <div className="text-[11px] text-black/40 mb-4">Auto-credited as ads render on your streams or you make impressions.</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {revenue.entries.length === 0 && <div className="text-sm text-black/40">No ad revenue yet.</div>}
          {revenue.entries.map((e) => (
            <div key={e.id} className="flex justify-between items-center text-xs border-b border-black/5 py-1.5 hover:bg-gray-50 px-2" data-testid={`biz-ledger-${e.id}`}>
              <div className="text-black/70">
                <span className="uppercase tracking-widest text-[9px] mr-2 text-black/40">{e.reason.replace(/_/g, " ")}</span>
                {new Date(e.ts).toLocaleString()}
              </div>
              <div className="font-mono text-green-600">+{e.amount_nx.toFixed(4)} NX</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
